import { z } from "zod";
import { twinAuthoringArtifactDescriptorSchema, twinAuthoringArtifactRoleSchema, twinAuthoringDecisionV1Schema, twinAuthoringReleaseV1Schema, twinAuthoringRunEventV1Schema, twinAuthoringRunRecordSchema, twinAuthoringRunRequestV1Schema, } from "./twin-authoring.js";
import { twinAuthoringCatalogueSelectionSchema, twinAuthoringOperatorPlanInputSchema, twinAuthoringSelectedBaselineViewsSchema, twinAuthoringPreparationEventV1Schema, twinAuthoringPreparationRecordSchema, twinAuthoringPreparationRequestV1Schema, twinAuthoringPreparationRequestSchema, } from "./twin-authoring-preparations.js";
const { storageLocator: _privateStorageLocator, ...publicArtifactDescriptorShape } = twinAuthoringArtifactDescriptorSchema.shape;
export const twinAuthoringPublicArtifactMetadataSchema = z.object({
    artifactId: z.string().regex(/^ta_art_[0-9a-f]{32}$/u),
    role: twinAuthoringArtifactRoleSchema,
    attemptCount: z.number().int().nonnegative().safe(),
    ...publicArtifactDescriptorShape,
}).strict();
const twinAuthoringPreparationCreateBaseSchema = z.object({
    modelSelection: twinAuthoringPreparationRequestV1Schema.shape.modelSelection,
    idempotencyKey: twinAuthoringPreparationRequestV1Schema.shape.idempotencyKey,
    sourceSelection: twinAuthoringPreparationRequestV1Schema.shape.sourceSelection,
});
// A reviewed plan, an operator's or a catalogue's, is the run's author: such a request names only
// its evaluator.
const namesOnlyTheEvaluator = (modelSelection) => modelSelection?.author === undefined && modelSelection?.authorReasoning === undefined;
export const twinAuthoringPreparationCreateInputSchema = z.union([
    twinAuthoringPreparationCreateBaseSchema.extend({
        catalogue: twinAuthoringCatalogueSelectionSchema.optional(),
    }).strict().refine(({ catalogue, modelSelection }) => catalogue === undefined || namesOnlyTheEvaluator(modelSelection), { path: ["modelSelection"], message: "A catalogue plan authors the run: name only the evaluator model and reasoning." }),
    twinAuthoringPreparationCreateBaseSchema.extend({
        refinement: z.object({
            baselineReleaseId: z.string().regex(/^ta_rel_[0-9a-f]{32}$/u),
            prompt: z.string().trim().min(1).max(12_000),
            selectedBaselineViewNames: twinAuthoringSelectedBaselineViewsSchema.optional(),
            // A reviewed plan the run returns exactly instead of calling a model; the server binds it.
            operatorPlan: twinAuthoringOperatorPlanInputSchema.optional(),
        }).strict(),
    }).strict().refine(({ modelSelection, refinement }) => refinement.operatorPlan === undefined
        || namesOnlyTheEvaluator(modelSelection), { path: ["modelSelection"], message: "A reviewed plan authors the run: name only the evaluator model and reasoning." }),
]);
export const twinAuthoringPreparationCreateResponseSchema = z.object({
    preparation: twinAuthoringPreparationRecordSchema,
    request: twinAuthoringPreparationRequestSchema,
}).strict();
export const twinAuthoringPreparationListResponseSchema = z.object({
    preparations: z.array(twinAuthoringPreparationRecordSchema).max(100),
}).strict();
export const twinAuthoringPreparationDetailResponseSchema = twinAuthoringPreparationCreateResponseSchema.extend({
    events: z.array(twinAuthoringPreparationEventV1Schema).max(10_000),
}).strict();
export const twinAuthoringPreparationCancelResponseSchema = z.object({
    preparation: twinAuthoringPreparationRecordSchema,
}).strict();
export const twinAuthoringRunListResponseSchema = z.object({
    runs: z.array(twinAuthoringRunRecordSchema).max(100),
}).strict();
export const twinAuthoringRunDetailResponseSchema = z.object({
    run: twinAuthoringRunRecordSchema,
    request: twinAuthoringRunRequestV1Schema,
    events: z.array(twinAuthoringRunEventV1Schema).max(10_000),
    decision: twinAuthoringDecisionV1Schema.nullable(),
    release: twinAuthoringReleaseV1Schema.nullable(),
    artifacts: z.array(twinAuthoringPublicArtifactMetadataSchema).max(128),
}).strict();
export const twinAuthoringRunCancelResponseSchema = z.object({
    run: twinAuthoringRunRecordSchema,
}).strict();
export const twinAuthoringArtifactListResponseSchema = z.object({
    artifacts: z.array(twinAuthoringPublicArtifactMetadataSchema).max(128),
}).strict();
